import React, { useCallback, useMemo, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../ui/dialog";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "../ui/select";
import { Textarea } from "../ui/textarea";
import { Button } from "../ui/button";
import { Pencil, Check, X, Loader2, Mail, CalendarPlus } from "lucide-react";
import api from "../../lib/api";
import { toast } from "sonner";

const TONE = {
  green: "bg-emerald-50 text-emerald-800 border-emerald-100",
  amber: "bg-amber-50 text-amber-800 border-amber-100",
  red: "bg-red-50 text-red-700 border-red-100",
  blue: "bg-blue-50 text-blue-700 border-blue-100",
  gray: "bg-gray-100 text-gray-700 border-gray-200",
};

const Pill = ({ tone = "gray", children, testid }) => (
  <span
    className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold border ${TONE[tone]}`}
    data-testid={testid}
  >
    {children}
  </span>
);

const FLAG_META = {
  trial_ending_soon: { tone: "amber", label: "Trial ending soon" },
  trial_expired: { tone: "red", label: "Trial expired" },
  no_leads_yet: { tone: "gray", label: "No leads yet" },
  inactive_7_days: { tone: "amber", label: "Inactive 7d" },
  high_usage: { tone: "green", label: "High usage" },
  paid_user: { tone: "blue", label: "Paid" },
};

const STATUS_OPTIONS = [
  { value: "_none", label: "Status: —" },
  { value: "new", label: "New" },
  { value: "contacted", label: "Contacted" },
  { value: "interested", label: "Interested" },
  { value: "needs_help", label: "Needs help" },
  { value: "not_interested", label: "Not interested" },
  { value: "converted", label: "Converted" },
  { value: "watch_later", label: "Watch later" },
];

const PRIORITY_OPTIONS = [
  { value: "_none", label: "Priority: —" },
  { value: "low", label: "Low" },
  { value: "normal", label: "Normal" },
  { value: "high", label: "High" },
];

const STATUS_TONE = {
  contacted: "blue",
  interested: "green",
  needs_help: "amber",
  not_interested: "gray",
  converted: "green",
  watch_later: "gray",
  new: "gray",
};

const fmtDate = (v) => {
  if (!v) return "—";
  try {
    return new Date(v).toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch {
    return "—";
  }
};

const fmtRelative = (v) => {
  // Short "in 2d" / "3d ago" — used for follow-up indicators.
  if (!v) return null;
  const t = new Date(v).getTime();
  if (Number.isNaN(t)) return null;
  const diffMs = t - Date.now();
  const diffDays = Math.round(diffMs / (1000 * 60 * 60 * 24));
  if (diffDays === 0) return "today";
  if (diffDays === 1) return "tomorrow";
  if (diffDays === -1) return "yesterday";
  if (diffDays > 0) return `in ${diffDays}d`;
  return `${Math.abs(diffDays)}d ago`;
};

const toIsoEndOfDay = (dateStr) => {
  // dateStr from <input type=date> is "YYYY-MM-DD" in local timezone.
  // Anchor to 09:00 local so reminders feel "morning-ish" without timezone
  // weirdness for the admin doing the entry.
  if (!dateStr) return null;
  const d = new Date(`${dateStr}T09:00:00`);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
};

const toInputDate = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

const NoteDialog = ({ open, onOpenChange, user, onSaved }) => {
  const [note, setNote] = useState(user?.admin_note || "");
  const [saving, setSaving] = useState(false);

  // Sync incoming user note when dialog opens for a different user
  React.useEffect(() => {
    setNote(user?.admin_note || "");
  }, [user?.user_id, user?.admin_note]);

  if (!user) return null;

  const save = async () => {
    setSaving(true);
    try {
      const payload = { admin_note: note.trim() ? note.trim() : null };
      const { data } = await api.patch(
        `/admin/users/${user.user_id}/retention`,
        payload,
      );
      toast.success("Note saved.");
      onSaved(user.user_id, data);
      onOpenChange(false);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(
        typeof detail === "string"
          ? detail
          : "Couldn't save the note. Try again.",
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="admin-note-dialog">
        <DialogHeader>
          <DialogTitle>Internal note · {user.business_name || user.email}</DialogTitle>
        </DialogHeader>
        <div className="space-y-2">
          <Textarea
            rows={6}
            maxLength={1000}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Add an internal note for this customer (max 1000 chars). Visible to admins only."
            data-testid="admin-note-textarea"
          />
          <p className="text-[11px] text-gray-500 text-right">
            {note.length}/1000
          </p>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
            Cancel
          </Button>
          <Button
            onClick={save}
            disabled={saving}
            className="bg-emerald-700 hover:bg-emerald-800 text-white"
            data-testid="admin-note-save"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save note"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const AttentionCard = ({ user, onPatched }) => {
  const [savingField, setSavingField] = useState("");
  const [noteOpen, setNoteOpen] = useState(false);
  // Local "extending" flag — shown briefly on the corresponding button while
  // POST /admin/users/:id/extend-trial is in flight.
  const [extending, setExtending] = useState(0);

  const patch = useCallback(
    async (field, body, friendlyField) => {
      setSavingField(field);
      try {
        const { data } = await api.patch(
          `/admin/users/${user.user_id}/retention`,
          body,
        );
        onPatched(user.user_id, data);
      } catch (err) {
        const detail = err?.response?.data?.detail;
        toast.error(
          typeof detail === "string"
            ? detail
            : `Couldn't update ${friendlyField || "field"}.`,
        );
      } finally {
        setSavingField("");
      }
    },
    [user.user_id, onPatched],
  );

  const onStatus = (val) => {
    const normalized = val === "_none" ? null : val;
    // Watch later semantics (Feb 2026 UX pass): when the admin switches
    // a user to "watch_later" without an existing future follow-up, we
    // default the follow-up to 30 days out. The backend queue builder
    // suppresses all retention signals for watch_later users until the
    // follow-up date is due, so having a date is what actually makes
    // the snooze work.
    const payload = { admin_status: normalized };
    if (normalized === "watch_later") {
      const existing = user.admin_follow_up_at
        ? new Date(user.admin_follow_up_at).getTime()
        : 0;
      // "Future" means > now + 24h — anything sooner would immediately
      // trigger due_today/followup_overdue, defeating the snooze intent.
      const future = Date.now() + 24 * 60 * 60 * 1000;
      if (!existing || existing <= future) {
        const d = new Date();
        d.setDate(d.getDate() + 30);
        d.setHours(9, 0, 0, 0);
        payload.admin_follow_up_at = d.toISOString();
      }
    }
    return patch("status", payload, "status");
  };
  const onPriority = (val) =>
    patch(
      "priority",
      { admin_priority: val === "_none" ? null : val },
      "priority",
    );
  const onContactedToday = () =>
    patch(
      "contacted",
      { admin_last_contacted_at: new Date().toISOString() },
      "last contacted",
    );
  const onFollowupOffset = (days) => {
    const d = new Date();
    d.setDate(d.getDate() + days);
    d.setHours(9, 0, 0, 0);
    return patch(
      "followup",
      { admin_follow_up_at: d.toISOString() },
      "follow-up",
    );
  };
  const onFollowupDateInput = (e) =>
    patch(
      "followup",
      { admin_follow_up_at: toIsoEndOfDay(e.target.value) },
      "follow-up",
    );
  const onClearFollowup = () =>
    patch("followup", { admin_follow_up_at: null }, "follow-up");

  // Visual cues
  const isHighPriority = user.admin_priority === "high";
  const followUpTime = user.admin_follow_up_at
    ? new Date(user.admin_follow_up_at).getTime()
    : null;
  const now = Date.now();
  const isOverdue = followUpTime && followUpTime < now;
  const isDueSoon =
    followUpTime &&
    followUpTime >= now &&
    followUpTime - now <= 24 * 60 * 60 * 1000;
  // Relative phrase for a future follow-up (e.g. "today", "tomorrow", "in 3d").
  // Powers the prominent "Follow-up tomorrow / in 3 days" header pill.
  const followUpRelative =
    followUpTime && followUpTime >= now ? fmtRelative(user.admin_follow_up_at) : null;

  // "Contacted today" — true iff admin_last_contacted_at is within the
  // current local calendar day. Drives the prominent green header pill so
  // the admin sees instantly that their click worked.
  const contactedToday = useMemo(() => {
    if (!user.admin_last_contacted_at) return false;
    const t = new Date(user.admin_last_contacted_at);
    if (Number.isNaN(t.getTime())) return false;
    const today = new Date();
    return (
      t.getFullYear() === today.getFullYear() &&
      t.getMonth() === today.getMonth() &&
      t.getDate() === today.getDate()
    );
  }, [user.admin_last_contacted_at]);

  const adminStatusTone = STATUS_TONE[user.admin_status] || "gray";

  // ===== Mailto template builder =====
  // Picks the most-relevant template for the user's current state so the
  // admin's mail client opens with a sensible subject + body already filled
  // in. The admin can edit before sending — we never send from the app.
  const buildMailto = useCallback(() => {
    const biz = user.business_name || "there";
    const flags = new Set(user.reason_flags || []);
    let subject = "Checking in on Tasklio";
    let body =
      `Hi ${biz}, just checking in to see if Tasklio is helping you respond ` +
      `faster and book more cleaning jobs.`;

    if (flags.has("trial_expired")) {
      subject = "Quick help with your Tasklio trial";
      body =
        `Hi ${biz}, I noticed your Tasklio trial ended. I wanted to check if ` +
        `you had a chance to test replies, quotes, review requests, or ` +
        `rebooking reminders. I'm happy to help you set it up.`;
    } else if (flags.has("no_leads_yet")) {
      subject = "Want help adding your first lead?";
      body =
        `Hi ${biz}, I noticed you haven't added a lead yet. If you want, I ` +
        `can help you add your first customer request and show how Tasklio ` +
        `generates replies and quotes.`;
    } else if (flags.has("trial_ending_soon")) {
      subject = "Your Tasklio trial is ending soon";
      body =
        `Hi ${biz}, your Tasklio trial is ending soon. I wanted to see if you ` +
        `need help getting value from the dashboard before it ends.`;
    } else if (flags.has("paid_user")) {
      subject = "Checking in on Tasklio";
      body =
        `Hi ${biz}, just checking in to see if Tasklio is helping you respond ` +
        `faster and book more cleaning jobs.`;
    }
    const params = new URLSearchParams({ subject, body });
    return `mailto:${encodeURIComponent(user.email || "")}?${params.toString()}`;
  }, [user.email, user.business_name, user.reason_flags]);

  const onOpenEmail = () => {
    // window.open with a `mailto:` opens the user's default mail client.
    // No payload is sent from the app — the admin types and sends manually.
    const href = buildMailto();
    window.open(href, "_self");
  };

  const onExtendTrial = async (days) => {
    setExtending(days);
    try {
      const { data } = await api.post(
        `/admin/users/${user.user_id}/extend-trial`,
        { days },
      );
      // The /admin/users/:id/extend-trial endpoint returns AdminUserDetail
      // which has trial_ends_at + trial_status. Merge those into the
      // attention-row shape so the card reflects the new dates instantly.
      onPatched(user.user_id, {
        trial_ends_at: data?.trial_ends_at ?? null,
        trial_status: data?.trial_status ?? "active",
      });
      toast.success(`Trial extended by ${days} days.`);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(
        typeof detail === "string" ? detail : "Couldn't extend the trial.",
      );
    } finally {
      setExtending(0);
    }
  };

  return (
    <div
      className={`rounded-xl border ${
        isHighPriority
          ? "border-red-200 bg-red-50/30"
          : "border-gray-200 bg-white"
      } p-4 transition-colors`}
      data-testid={`admin-attention-row-${user.user_id}`}
    >
      {/* Row 1: identity + server status + flags */}
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-medium text-gray-900 truncate">
              {user.business_name || "—"}
            </span>
            {user.subscription_status === "active" ? (
              <Pill tone="green">Active sub</Pill>
            ) : null}
            {user.admin_status ? (
              <Pill
                tone={adminStatusTone}
                testid={`admin-status-pill-${user.user_id}`}
              >
                {STATUS_OPTIONS.find((o) => o.value === user.admin_status)?.label || user.admin_status}
              </Pill>
            ) : null}
            {isHighPriority ? (
              <Pill tone="red" testid={`admin-priority-pill-${user.user_id}`}>
                High priority
              </Pill>
            ) : null}
            {isOverdue ? (
              <Pill tone="red" testid={`admin-followup-overdue-${user.user_id}`}>
                Follow-up overdue
              </Pill>
            ) : isDueSoon ? (
              <Pill tone="amber" testid={`admin-followup-duesoon-${user.user_id}`}>
                Follow-up {followUpRelative}
              </Pill>
            ) : followUpRelative ? (
              <Pill tone="blue" testid={`admin-followup-scheduled-${user.user_id}`}>
                Follow-up {followUpRelative}
              </Pill>
            ) : null}
            {contactedToday ? (
              <Pill tone="green" testid={`admin-contacted-today-${user.user_id}`}>
                <Check className="w-3 h-3 mr-1" /> Contacted today
              </Pill>
            ) : null}
          </div>
          <div className="text-xs text-gray-500 mt-0.5 flex items-center gap-1.5 flex-wrap">
            <span className="truncate">{user.email}</span>
            {user.acquisition_source ? (
              <span
                className="inline-flex items-center px-1.5 py-0.5 rounded-full bg-violet-50 border border-violet-100 text-violet-700 text-[10px] font-semibold uppercase tracking-wider"
                title={`Signed up from ?source=${user.acquisition_source}`}
                data-testid={`admin-attention-source-${user.user_id}`}
              >
                via {user.acquisition_source}
              </span>
            ) : null}
          </div>
        </div>
        <div className="flex flex-wrap gap-1.5 justify-end">
          {(user.reason_flags || []).length === 0 ? (
            // Filtered-population users may not have any urgency flags. Show
            // a neutral "Healthy" pill so the card doesn't look broken.
            <Pill tone="gray" testid={`admin-attention-flag-healthy-${user.user_id}`}>
              Healthy
            </Pill>
          ) : (
            (user.reason_flags || []).map((f) => {
              const meta = FLAG_META[f] || { tone: "gray", label: f };
              return (
                <Pill
                  key={f}
                  tone={meta.tone}
                  testid={`admin-attention-flag-${f}-${user.user_id}`}
                >
                  {meta.label}
                </Pill>
              );
            })
          )}
        </div>
      </div>

      {/* Row 2: context line */}
      <div className="mt-2 text-[13px] text-gray-600 flex flex-wrap gap-x-3 gap-y-1">
        <span>
          <span className="text-gray-400">Trial ends:</span> {fmtDate(user.trial_ends_at)}
        </span>
        <span>
          <span className="text-gray-400">Usage:</span> {user.total_leads} leads
          <span className="text-gray-400"> · </span>
          {user.booked_leads} booked
          <span className="text-gray-400"> · </span>
          {user.review_requests_sent} reviews
          <span className="text-gray-400"> · </span>
          {user.rebook_reminders_sent} rebook
        </span>
        {user.admin_last_contacted_at ? (
          <span>
            <span className="text-gray-400">Last contacted:</span>{" "}
            {fmtRelative(user.admin_last_contacted_at)}
          </span>
        ) : null}
      </div>

      {/* Row 3: admin retention controls */}
      <div className="mt-3 grid grid-cols-1 sm:grid-cols-12 gap-2 items-center">
        <div className="sm:col-span-3">
          <Select
            value={user.admin_status || "_none"}
            onValueChange={onStatus}
            disabled={savingField === "status"}
          >
            <SelectTrigger
              className="h-9 text-[13px]"
              data-testid={`admin-status-select-${user.user_id}`}
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {STATUS_OPTIONS.map((o) => (
                <SelectItem key={o.value} value={o.value}>
                  {o.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {/* Contextual hint under the status dropdown so the admin knows
              exactly what each terminal-ish status does to the queue.
              Watch later auto-defaults follow-up to 30 days when picked
              without a date — surface that so it doesn't feel magical. */}
          {user.admin_status === "watch_later" ? (
            <p
              className="mt-1 text-[10px] text-gray-500 leading-tight"
              data-testid={`admin-status-hint-watch-later-${user.user_id}`}
            >
              Hidden from queue until follow-up date. Defaults to 30 days if no date is set.
            </p>
          ) : user.admin_status === "not_interested" ? (
            <p
              className="mt-1 text-[10px] text-gray-500 leading-tight"
              data-testid={`admin-status-hint-not-interested-${user.user_id}`}
            >
              Never appears in queue again until status is changed.
            </p>
          ) : user.admin_status === "converted" ? (
            <p
              className="mt-1 text-[10px] text-gray-500 leading-tight"
              data-testid={`admin-status-hint-converted-${user.user_id}`}
            >
              Never appears in queue again until status is changed.
            </p>
          ) : null}
        </div>
        <div className="sm:col-span-3">
          <Select
            value={user.admin_priority || "_none"}
            onValueChange={onPriority}
            disabled={savingField === "priority"}
          >
            <SelectTrigger
              className="h-9 text-[13px]"
              data-testid={`admin-priority-select-${user.user_id}`}
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {PRIORITY_OPTIONS.map((o) => (
                <SelectItem key={o.value} value={o.value}>
                  {o.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="sm:col-span-3 flex items-center gap-1.5">
          <input
            type="date"
            value={toInputDate(user.admin_follow_up_at)}
            onChange={onFollowupDateInput}
            disabled={savingField === "followup"}
            className="h-9 w-full rounded-md border border-gray-200 px-2 text-[13px] focus:outline-none focus:ring-2 focus:ring-emerald-600/20"
            data-testid={`admin-followup-date-${user.user_id}`}
            aria-label="Follow-up date"
          />
        </div>
        <div className="sm:col-span-3 flex items-center justify-end gap-1.5 flex-wrap">
          <button
            type="button"
            onClick={onContactedToday}
            disabled={savingField === "contacted"}
            className="text-[11px] font-semibold px-2 py-1 rounded-md border border-gray-200 hover:bg-emerald-50 hover:border-emerald-200 hover:text-emerald-800 text-gray-600 transition-colors"
            data-testid={`admin-quick-contacted-${user.user_id}`}
          >
            Contacted today
          </button>
          <button
            type="button"
            onClick={() => onFollowupOffset(1)}
            disabled={savingField === "followup"}
            className="text-[11px] font-semibold px-2 py-1 rounded-md border border-gray-200 hover:bg-emerald-50 hover:border-emerald-200 hover:text-emerald-800 text-gray-600 transition-colors"
            data-testid={`admin-quick-followup-1d-${user.user_id}`}
          >
            +1d
          </button>
          <button
            type="button"
            onClick={() => onFollowupOffset(3)}
            disabled={savingField === "followup"}
            className="text-[11px] font-semibold px-2 py-1 rounded-md border border-gray-200 hover:bg-emerald-50 hover:border-emerald-200 hover:text-emerald-800 text-gray-600 transition-colors"
            data-testid={`admin-quick-followup-3d-${user.user_id}`}
          >
            +3d
          </button>
          {user.admin_follow_up_at ? (
            <button
              type="button"
              onClick={onClearFollowup}
              disabled={savingField === "followup"}
              className="text-[11px] font-semibold px-2 py-1 rounded-md border border-gray-200 hover:bg-red-50 hover:border-red-200 hover:text-red-700 text-gray-600 transition-colors"
              data-testid={`admin-quick-clear-followup-${user.user_id}`}
              title="Clear follow-up"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          ) : null}
          <button
            type="button"
            onClick={() => setNoteOpen(true)}
            className={`text-[11px] font-semibold px-2 py-1 rounded-md border transition-colors ${
              user.admin_note
                ? "border-emerald-200 bg-emerald-50 text-emerald-800 hover:bg-emerald-100"
                : "border-gray-200 text-gray-600 hover:bg-emerald-50 hover:border-emerald-200 hover:text-emerald-800"
            }`}
            data-testid={`admin-note-edit-${user.user_id}`}
            title={user.admin_note ? "Edit note" : "Add note"}
          >
            <Pencil className="w-3.5 h-3.5 inline" />{" "}
            {user.admin_note ? "Note" : "Add note"}
          </button>
          {savingField ? (
            <Loader2
              className="w-3.5 h-3.5 animate-spin text-gray-400"
              data-testid={`admin-saving-${user.user_id}`}
            />
          ) : (
            savingField === "" && user.admin_status ? <Check className="w-3.5 h-3.5 text-emerald-600 opacity-0" /> : null
          )}
        </div>
      </div>

      {/* Row 4: admin-only "richer" actions — Email (mailto) + Extend trial.
          Lives in its own row so the dense quick-actions row above stays
          legible. Email opens the local mail client with a templated draft;
          nothing is ever sent from the app. */}
      <div
        className="mt-2 flex flex-wrap items-center gap-1.5 pt-2 border-t border-gray-100"
        data-testid={`admin-actions-${user.user_id}`}
      >
        <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider mr-1">
          Actions
        </span>
        <button
          type="button"
          onClick={onOpenEmail}
          disabled={!user.email}
          className="text-[11px] font-semibold px-2 py-1 rounded-md border border-gray-200 text-gray-700 hover:bg-emerald-50 hover:border-emerald-200 hover:text-emerald-800 transition-colors disabled:opacity-40 disabled:hover:bg-white inline-flex items-center gap-1"
          data-testid={`admin-action-email-${user.user_id}`}
          title="Opens your mail client with a templated draft. No email is sent from Tasklio."
        >
          <Mail className="w-3.5 h-3.5" /> Email
        </button>
        <button
          type="button"
          onClick={() => onExtendTrial(7)}
          disabled={!!extending}
          className="text-[11px] font-semibold px-2 py-1 rounded-md border border-amber-200 bg-amber-50 text-amber-800 hover:bg-amber-100 transition-colors inline-flex items-center gap-1"
          data-testid={`admin-action-extend-7-${user.user_id}`}
          title="Reset trial_ends_at to now + 7 days"
        >
          {extending === 7 ? (
            <Loader2 className="w-3.5 h-3.5 animate-spin" />
          ) : (
            <CalendarPlus className="w-3.5 h-3.5" />
          )}
          Extend +7d
        </button>
        <button
          type="button"
          onClick={() => onExtendTrial(30)}
          disabled={!!extending}
          className="text-[11px] font-semibold px-2 py-1 rounded-md border border-amber-200 bg-amber-50 text-amber-800 hover:bg-amber-100 transition-colors inline-flex items-center gap-1"
          data-testid={`admin-action-extend-30-${user.user_id}`}
          title="Reset trial_ends_at to now + 30 days"
        >
          {extending === 30 ? (
            <Loader2 className="w-3.5 h-3.5 animate-spin" />
          ) : (
            <CalendarPlus className="w-3.5 h-3.5" />
          )}
          Extend +30d
        </button>
      </div>

      {user.admin_note ? (
        <div
          className="mt-2 rounded-md border border-emerald-100 bg-emerald-50/50 px-3 py-2 text-[13px] text-gray-700"
          data-testid={`admin-note-preview-${user.user_id}`}
        >
          {user.admin_note}
        </div>
      ) : null}

      <NoteDialog
        open={noteOpen}
        onOpenChange={setNoteOpen}
        user={user}
        onSaved={onPatched}
      />
    </div>
  );
};

const FILTERS = [
  { value: "all", label: "All" },
  { value: "high_priority", label: "High priority" },
  { value: "followup_due", label: "Follow-up due" },
  { value: "no_leads_yet", label: "No leads yet" },
  { value: "trial_ending_soon", label: "Trial ending" },
  { value: "inactive_7_days", label: "Inactive" },
];

export const AdminAttentionList = ({ users, onPatched, externalActive = false, externalLabel = null }) => {
  const [filter, setFilter] = useState("all");

  const filtered = useMemo(() => {
    // When the parent already filtered via a metric-card click (externalActive),
    // skip the internal flag pills — they were derived for attention_users and
    // are meaningless against the full population.
    if (externalActive) return users;
    if (filter === "all") return users;
    if (filter === "high_priority")
      return users.filter((u) => u.admin_priority === "high");
    if (filter === "followup_due") {
      const now = Date.now();
      return users.filter((u) => {
        if (!u.admin_follow_up_at) return false;
        const t = new Date(u.admin_follow_up_at).getTime();
        // due-soon (<24h) OR overdue
        return Number.isFinite(t) && t - now <= 24 * 60 * 60 * 1000;
      });
    }
    return users.filter((u) => (u.reason_flags || []).includes(filter));
  }, [users, filter, externalActive]);

  return (
    <div className="px-5 pt-1 pb-5">
      <div className="flex items-center justify-between mb-3 mt-2 gap-3 flex-wrap">
        <h3 className="font-display text-sm font-semibold text-gray-900 uppercase tracking-wider">
          {externalActive ? "Filtered users" : "Needs Attention"}
        </h3>
        {externalActive ? (
          <span className="text-xs text-gray-500" data-testid="admin-attention-count">
            {filtered.length} user{filtered.length === 1 ? "" : "s"}
          </span>
        ) : (
          <div className="flex items-center gap-2 flex-wrap" data-testid="admin-attention-filters">
            {FILTERS.map((f) => (
              <button
                key={f.value}
                type="button"
                onClick={() => setFilter(f.value)}
                className={`text-[11px] font-semibold px-2.5 py-1 rounded-full border transition-colors ${
                  filter === f.value
                    ? "bg-emerald-700 text-white border-emerald-700"
                    : "bg-white text-gray-600 border-gray-200 hover:border-emerald-200 hover:text-emerald-800"
                }`}
                data-testid={`admin-attention-filter-${f.value}`}
              >
                {f.label}
              </button>
            ))}
            <span className="text-xs text-gray-500 ml-2">
              {filtered.length} of {users.length}
            </span>
          </div>
        )}
      </div>
      {filtered.length === 0 ? (
        <div
          className="rounded-lg border border-dashed border-gray-200 bg-gray-50 px-5 py-10 text-center text-gray-500"
          data-testid="admin-attention-empty"
        >
          {users.length === 0 ? (
            externalActive ? (
              <>
                No users match{" "}
                <span className="font-semibold">{externalLabel || "this filter"}</span>.
              </>
            ) : (
              "Nothing urgent right now. Customers look healthy."
            )
          ) : (
            "No users match this filter."
          )}
        </div>
      ) : (
        <div className="space-y-2.5" data-testid="admin-attention-list">
          {filtered.map((u) => (
            <AttentionCard key={u.user_id} user={u} onPatched={onPatched} />
          ))}
        </div>
      )}
    </div>
  );
};
